import { loadStripe, type Stripe, type StripeElements, type StripeCardElement } from "@stripe/stripe-js";

import type { PaymentIntentResponse } from "@/api/online-ordering-service/model";

let stripeInstance: Stripe | null = null;
let lastKey: string | null = null;

export async function getStripe(publicKey: string): Promise<Stripe> {
  if (stripeInstance && lastKey === publicKey) return stripeInstance;
  const stripe = await loadStripe(publicKey);
  if (!stripe) throw new Error("Failed to initialize Stripe");
  stripeInstance = stripe;
  lastKey = publicKey;
  return stripe;
}

export type StripeMount = {
  stripe: Stripe;
  elements: StripeElements;
  card: StripeCardElement;
  unmount: () => void;
};

export async function mountStripeCardElement(
  publicKey: string,
  elementId: string,
): Promise<StripeMount> {
  const stripe = await getStripe(publicKey);
  const elements = stripe.elements();
  const card = elements.create("card", {
    style: {
      base: {
        fontFamily: "Inter, sans-serif",
        fontSize: "16px",
        color: "#111827",
        "::placeholder": { color: "#9CA3AF" },
      },
    },
  });
  const container = document.getElementById(elementId);
  if (!container) throw new Error(`Stripe container #${elementId} not found`);
  card.mount(container);
  return {
    stripe,
    elements,
    card,
    unmount: () => {
      try {
        card.unmount();
      } catch {
        // ignore
      }
    },
  };
}

export async function confirmStripeCardPayment(
  intent: PaymentIntentResponse,
  mount: StripeMount,
): Promise<{ ok: boolean; error?: string }> {
  if (!intent.clientSecret) {
    return { ok: false, error: "Missing client secret" };
  }
  const result = await mount.stripe.confirmCardPayment(intent.clientSecret, {
    payment_method: { card: mount.card },
  });
  if (result.error) {
    return { ok: false, error: result.error.message ?? "Payment failed" };
  }
  if (result.paymentIntent && (result.paymentIntent.status === "succeeded" || result.paymentIntent.status === "requires_capture")) {
    return { ok: true };
  }
  return { ok: false, error: `Unexpected status: ${result.paymentIntent?.status ?? "unknown"}` };
}

export async function confirmStripeSavedCardPayment(
  intent: PaymentIntentResponse,
  returnUrl?: string,
): Promise<{ ok: boolean; error?: string }> {
  if (!intent.publicKey) {
    return { ok: false, error: "Missing Stripe public key" };
  }
  if (!intent.clientSecret) {
    return { ok: false, error: "Missing client secret" };
  }
  const stripe = await getStripe(intent.publicKey);
  const data = returnUrl
    ? ({ return_url: returnUrl } as unknown as Parameters<Stripe["confirmCardPayment"]>[1])
    : undefined;
  const result = await stripe.confirmCardPayment(intent.clientSecret, data);
  if (result.error) {
    return { ok: false, error: result.error.message ?? "Payment failed" };
  }
  if (
    result.paymentIntent &&
    (result.paymentIntent.status === "succeeded" ||
      result.paymentIntent.status === "requires_capture")
  ) {
    return { ok: true };
  }
  return {
    ok: false,
    error: `Unexpected status: ${result.paymentIntent?.status ?? "unknown"}`,
  };
}
